
import React, { useState, useEffect } from 'react';
import { Store, Product, Category } from '../types';
import { storage } from '../lib/storage';
import { ChevronLeft, Plus, Heart, Star, LayoutGrid, Info, ArrowRight, Package, Sparkles } from 'lucide-react';

interface StoreProfileProps {
  store: Store;
  onAddToCart: (product: Product) => void;
  onViewProduct: (product: Product) => void;
  onBack: () => void;
}

const StoreProfile: React.FC<StoreProfileProps> = ({ store, onAddToCart, onViewProduct, onBack }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    const storeProducts = storage.getProducts().filter(p => p.storeId === store.id);
    const storeCategories = storage.getCategories().filter(c => c.storeId === store.id);
    setProducts(storeProducts);
    setCategories(storeCategories);
    window.scrollTo(0, 0);
  }, [store.id]);

  const primaryColor = store.branding?.primaryColor || '#2563eb';

  return (
    <div className="pb-20">
      {/* Banner */}
      <div className="h-80 relative overflow-hidden bg-gray-900">
        <div className="absolute inset-0">
           {store.branding?.bannerImage ? (
             <img src={store.branding.bannerImage} alt="Banner" className="w-full h-full object-cover" />
           ) : (
             <div className="w-full h-full bg-gradient-to-r from-gray-900 to-gray-800"></div>
           )}
           <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex items-end pb-16">
          <button 
            onClick={onBack}
            className="absolute top-8 left-4 sm:left-8 flex items-center gap-2 bg-white/10 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-bold border border-white/20 hover:bg-white/20 transition-all"
          >
            <ChevronLeft className="h-4 w-4" /> Back to Marketplace
          </button>
          
          {store.branding?.slogan && (
             <p className="text-white/80 font-black text-xl uppercase tracking-widest flex items-center gap-2 animate-in slide-in-from-left duration-700">
                <Sparkles className="h-5 w-5" style={{ color: primaryColor }} /> {store.branding.slogan}
             </p>
          )}
        </div>
      </div>

      {/* Header Info */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-24 relative z-10">
        <div className="bg-white rounded-[40px] shadow-2xl p-8 sm:p-12 border border-gray-100 flex flex-col md:flex-row items-center md:items-end gap-10">
           <div 
             className="w-44 h-44 rounded-[3.5rem] shadow-2xl border-[6px] border-white flex items-center justify-center text-6xl font-black text-white shrink-0 transform -rotate-3 hover:rotate-0 transition-transform duration-500"
             style={{ backgroundColor: primaryColor }}
           >
             {store.name[0].toUpperCase()}
           </div>
           <div className="flex-grow text-center md:text-left space-y-4">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <h1 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">{store.name}</h1>
                <span 
                  className="text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest self-center md:self-auto"
                  style={{ backgroundColor: primaryColor + '15', color: primaryColor }}
                >
                  Verified Seller
                </span>
              </div>
              <p className="text-gray-500 font-medium max-w-xl">Curated collection from {store.name}. Discover premium gadgets with dedicated support and world-class quality.</p>
              <div className="flex items-center justify-center md:justify-start gap-8 pt-2">
                 <div>
                   <p className="text-2xl font-black text-gray-900">{products.length}</p>
                   <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Products</p>
                 </div>
                 <div className="w-px h-8 bg-gray-100"></div>
                 <div>
                   <p className="text-2xl font-black text-gray-900">4.9</p>
                   <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Rating</p>
                 </div>
                 <div className="w-px h-8 bg-gray-100"></div>
                 <div>
                   <p className="text-2xl font-black text-gray-900">12k+</p>
                   <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Followers</p>
                 </div>
              </div>
           </div>
           <button 
             className="text-white px-10 py-5 rounded-[2rem] font-black shadow-2xl transition-all hover:scale-105 active:scale-95"
             style={{ backgroundColor: primaryColor, boxShadow: `0 20px 40px -10px ${primaryColor}50` }}
           >
             Follow Store
           </button>
        </div>
      </div>

      {/* Product Collection */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24 space-y-12">
         <div className="flex items-center justify-between border-b border-gray-100 pb-8">
            <div className="flex items-center gap-3">
               <LayoutGrid className="h-6 w-6" style={{ color: primaryColor }} />
               <h2 className="text-2xl font-black text-gray-900 tracking-tight">Shop the Collection</h2>
            </div>
            <div className="flex items-center gap-2">
               <span className="text-sm font-bold text-gray-400">Sort:</span>
               <select className="bg-transparent border-none font-black text-sm focus:ring-0 cursor-pointer">
                  <option>Latest Arrivals</option>
                  <option>Price: Low to High</option>
                  <option>Best Rated</option>
               </select>
            </div>
         </div>

         {products.length === 0 ? (
           <div className="py-20 text-center bg-gray-50 rounded-[3rem] border border-dashed border-gray-200">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-bold">This vendor hasn't uploaded any products yet.</p>
           </div>
         ) : (
           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
              {products.map(product => (
                <div 
                  key={product.id}
                  className="group cursor-pointer space-y-5"
                  onClick={() => onViewProduct(product)}
                >
                   <div className="aspect-[3/4] bg-gray-100 rounded-[3rem] overflow-hidden relative shadow-sm border border-gray-50">
                      <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                      <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0">
                        <button className="p-4 bg-white shadow-2xl rounded-2xl text-gray-400 hover:text-red-500 active:scale-90 transition-all">
                          <Heart className="h-6 w-6" />
                        </button>
                      </div>
                      <div className="absolute bottom-6 left-6 right-6 opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0">
                         {/* Fix: removed '--hover-bg' custom property from style object as it is not a valid TypeScript CSS property and is unused here. Hover state is handled imperatively. */}
                         <button 
                            onClick={(e) => { e.stopPropagation(); onAddToCart(product); }}
                            className="w-full bg-white text-gray-900 py-4 rounded-2xl text-xs font-black uppercase tracking-widest shadow-2xl hover:text-white transition-all"
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = primaryColor}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                         >
                            Add to Cart
                         </button>
                      </div>
                   </div>
                   <div className="px-2 space-y-2">
                      <div className="flex justify-between items-start">
                        <h3 className="font-black text-lg text-gray-900 group-hover:text-blue-600 transition-colors leading-tight max-w-[70%]">{product.name}</h3>
                        <span className="font-black text-xl text-gray-900">${product.price}</span>
                      </div>
                      <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">
                        {categories.find(c => c.id === product.categoryId)?.name || 'Premium Gadget'}
                      </p>
                   </div>
                </div>
              ))}
           </div>
         )}
      </div>

      {/* Store Trust - Branded */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-32">
         <div className="bg-white border-2 border-gray-100 rounded-[4rem] p-12 md:p-20 grid grid-cols-1 md:grid-cols-3 gap-16">
            <div className="space-y-6">
               <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: primaryColor + '15', color: primaryColor }}>
                  <Info className="h-8 w-8" />
               </div>
               <h3 className="text-2xl font-black text-gray-900">Our Story</h3>
               <p className="text-gray-500 text-sm leading-relaxed font-medium">Verified by Lynqar since 2024. We specialize in sourcing authentic, high-performance tech that meets the highest standards of our global community.</p>
            </div>
            <div className="space-y-6">
               <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: primaryColor + '15', color: primaryColor }}>
                  <Star className="h-8 w-8" />
               </div>
               <h3 className="text-2xl font-black text-gray-900">Quality Guard</h3>
               <p className="text-gray-500 text-sm leading-relaxed font-medium">Every unit shipped from our warehouse undergoes a 12-point quality inspection. You receive only the best, guaranteed.</p>
            </div>
            <div className="space-y-6">
               <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: primaryColor + '15', color: primaryColor }}>
                  <Package className="h-8 w-8" />
               </div>
               <h3 className="text-2xl font-black text-gray-900">Fast Fulfillment</h3>
               <p className="text-gray-500 text-sm leading-relaxed font-medium">Our logistics pipeline is optimized for speed. 95% of orders are dispatched within 24 hours of confirmation.</p>
            </div>
         </div>
      </div>
    </div>
  );
};

export default StoreProfile;
